import {
  DESIGN_TOKEN_SCHEMA_VERSION,
  TYPOGRAPHY_FAMILIES,
  TYPOGRAPHY_ROLES,
  type SupportedFontWeight,
  type TypographyRole,
} from './source';

export const TYPOGRAPHY_ADAPTER_PROFILE_VERSION = '1.0.0-candidate.1' as const;

export const TYPOGRAPHY_CHANNELS = ['browser', 'native', 'email', 'pdf'] as const;
export type TypographyChannel = (typeof TYPOGRAPHY_CHANNELS)[number];

export interface FontFaceEvidence {
  readonly family: keyof typeof TYPOGRAPHY_FAMILIES;
  readonly weight: SupportedFontWeight;
  readonly style: 'normal';
  readonly source: string;
  readonly sha256: string;
}

export interface TypographyAdapterProfile {
  readonly version: typeof TYPOGRAPHY_ADAPTER_PROFILE_VERSION;
  readonly tokenSchemaVersion: typeof DESIGN_TOKEN_SCHEMA_VERSION;
  readonly channel: TypographyChannel;
  readonly status: 'candidate_only';
  readonly delivery: 'embedded_files' | 'package_bundled_files' | 'fallback_stack_only';
  readonly targets: readonly string[];
  readonly familyStacks: {
    readonly display: readonly string[];
    readonly interface: readonly string[];
  };
  readonly permittedWeights: readonly SupportedFontWeight[];
  readonly faceEvidence: readonly FontFaceEvidence[];
  readonly checksumPolicy:
    | 'EVERY_EMBEDDED_OR_BUNDLED_FACE_SHA256_BOUND'
    | 'NOT_APPLICABLE_NO_FONT_BYTES_EMBEDDED';
  readonly unsupportedWeightSubstitutions: Readonly<
    Partial<Record<SupportedFontWeight, SupportedFontWeight>>
  >;
  readonly syntheticWeightsAllowed: false;
}

const browserFaceEvidence = [
  {
    family: 'display',
    weight: 700,
    style: 'normal',
    source: 'apps/web/src/app/fonts/Merriweather_700Bold.ttf',
    sha256: 'd9587d1266fb18da859f44a0ada7bdc5e7cb68af29ea87aa7caab2607cd74579',
  },
  {
    family: 'display',
    weight: 900,
    style: 'normal',
    source: 'apps/web/src/app/fonts/Merriweather_900Black.ttf',
    sha256: 'fcf5fab0dead0f615f5e6502e1a385b887224215901b70e2ae9428103f321dac',
  },
  {
    family: 'interface',
    weight: 400,
    style: 'normal',
    source: 'apps/web/src/app/fonts/SpaceGrotesk_400Regular.ttf',
    sha256: '637578d47b0e70e02b3eb5056847dd9add17bd683234d28f5d11a6cf66ff2725',
  },
  {
    family: 'interface',
    weight: 500,
    style: 'normal',
    source: 'apps/web/src/app/fonts/SpaceGrotesk_500Medium.ttf',
    sha256: 'd8135bce6603398f3d0cdb478a018d7fa869b0168d037a9408cad23e030bfa7a',
  },
  {
    family: 'interface',
    weight: 600,
    style: 'normal',
    source: 'apps/web/src/app/fonts/SpaceGrotesk_600SemiBold.ttf',
    sha256: '279a5d22b8ab377f41706af566610667db3941b069b50187b98af09ca3b08f91',
  },
  {
    family: 'interface',
    weight: 700,
    style: 'normal',
    source: 'apps/web/src/app/fonts/SpaceGrotesk_700Bold.ttf',
    sha256: '8fb63865c6afd083723dc1775548306b7751f20397bebcb79646032c598c81b9',
  },
  {
    family: 'display',
    weight: 700,
    style: 'normal',
    source: 'apps/client-portal/src/app/fonts/Merriweather_700Bold.ttf',
    sha256: 'd9587d1266fb18da859f44a0ada7bdc5e7cb68af29ea87aa7caab2607cd74579',
  },
  {
    family: 'display',
    weight: 900,
    style: 'normal',
    source: 'apps/client-portal/src/app/fonts/Merriweather_900Black.ttf',
    sha256: 'fcf5fab0dead0f615f5e6502e1a385b887224215901b70e2ae9428103f321dac',
  },
  {
    family: 'interface',
    weight: 400,
    style: 'normal',
    source: 'apps/client-portal/src/app/fonts/SpaceGrotesk_400Regular.ttf',
    sha256: '637578d47b0e70e02b3eb5056847dd9add17bd683234d28f5d11a6cf66ff2725',
  },
  {
    family: 'interface',
    weight: 500,
    style: 'normal',
    source: 'apps/client-portal/src/app/fonts/SpaceGrotesk_500Medium.ttf',
    sha256: 'd8135bce6603398f3d0cdb478a018d7fa869b0168d037a9408cad23e030bfa7a',
  },
  {
    family: 'interface',
    weight: 600,
    style: 'normal',
    source: 'apps/client-portal/src/app/fonts/SpaceGrotesk_600SemiBold.ttf',
    sha256: '279a5d22b8ab377f41706af566610667db3941b069b50187b98af09ca3b08f91',
  },
  {
    family: 'interface',
    weight: 700,
    style: 'normal',
    source: 'apps/client-portal/src/app/fonts/SpaceGrotesk_700Bold.ttf',
    sha256: '8fb63865c6afd083723dc1775548306b7751f20397bebcb79646032c598c81b9',
  },
] as const satisfies readonly FontFaceEvidence[];

const nativeFaceEvidence = [
  {
    family: 'display',
    weight: 400,
    style: 'normal',
    source: '@expo-google-fonts/merriweather@0.4.2/400Regular/Merriweather_400Regular.ttf',
    sha256: 'f1541057f1765ffa5ee7eb873f01bec1dd2238c85a8b1602c462fd8eda92b6df',
  },
  {
    family: 'display',
    weight: 700,
    style: 'normal',
    source: '@expo-google-fonts/merriweather@0.4.2/700Bold/Merriweather_700Bold.ttf',
    sha256: 'd9587d1266fb18da859f44a0ada7bdc5e7cb68af29ea87aa7caab2607cd74579',
  },
  {
    family: 'interface',
    weight: 400,
    style: 'normal',
    source: '@expo-google-fonts/space-grotesk@0.4.1/400Regular/SpaceGrotesk_400Regular.ttf',
    sha256: '637578d47b0e70e02b3eb5056847dd9add17bd683234d28f5d11a6cf66ff2725',
  },
  {
    family: 'interface',
    weight: 500,
    style: 'normal',
    source: '@expo-google-fonts/space-grotesk@0.4.1/500Medium/SpaceGrotesk_500Medium.ttf',
    sha256: 'd8135bce6603398f3d0cdb478a018d7fa869b0168d037a9408cad23e030bfa7a',
  },
  {
    family: 'interface',
    weight: 600,
    style: 'normal',
    source: '@expo-google-fonts/space-grotesk@0.4.1/600SemiBold/SpaceGrotesk_600SemiBold.ttf',
    sha256: '279a5d22b8ab377f41706af566610667db3941b069b50187b98af09ca3b08f91',
  },
  {
    family: 'interface',
    weight: 700,
    style: 'normal',
    source: '@expo-google-fonts/space-grotesk@0.4.1/700Bold/SpaceGrotesk_700Bold.ttf',
    sha256: '8fb63865c6afd083723dc1775548306b7751f20397bebcb79646032c598c81b9',
  },
] as const satisfies readonly FontFaceEvidence[];

const displayStack = [
  TYPOGRAPHY_FAMILIES.display.name,
  ...TYPOGRAPHY_FAMILIES.display.fallbackStack,
] as const;
const interfaceStack = [
  TYPOGRAPHY_FAMILIES.interface.name,
  ...TYPOGRAPHY_FAMILIES.interface.fallbackStack,
] as const;

export const TYPOGRAPHY_ADAPTER_PROFILES = {
  browser: {
    version: TYPOGRAPHY_ADAPTER_PROFILE_VERSION,
    tokenSchemaVersion: DESIGN_TOKEN_SCHEMA_VERSION,
    channel: 'browser',
    status: 'candidate_only',
    delivery: 'embedded_files',
    targets: ['provider-web', 'client-portal'],
    familyStacks: { display: displayStack, interface: interfaceStack },
    permittedWeights: [400, 500, 600, 700, 900],
    faceEvidence: browserFaceEvidence,
    checksumPolicy: 'EVERY_EMBEDDED_OR_BUNDLED_FACE_SHA256_BOUND',
    unsupportedWeightSubstitutions: {},
    syntheticWeightsAllowed: false,
  },
  native: {
    version: TYPOGRAPHY_ADAPTER_PROFILE_VERSION,
    tokenSchemaVersion: DESIGN_TOKEN_SCHEMA_VERSION,
    channel: 'native',
    status: 'candidate_only',
    delivery: 'package_bundled_files',
    targets: ['care-worker-app'],
    familyStacks: { display: displayStack, interface: interfaceStack },
    permittedWeights: [400, 500, 600, 700],
    faceEvidence: nativeFaceEvidence,
    checksumPolicy: 'EVERY_EMBEDDED_OR_BUNDLED_FACE_SHA256_BOUND',
    unsupportedWeightSubstitutions: { 900: 700 },
    syntheticWeightsAllowed: false,
  },
  email: {
    version: TYPOGRAPHY_ADAPTER_PROFILE_VERSION,
    tokenSchemaVersion: DESIGN_TOKEN_SCHEMA_VERSION,
    channel: 'email',
    status: 'candidate_only',
    delivery: 'fallback_stack_only',
    targets: ['transactional-email', 'auth-email'],
    familyStacks: { display: displayStack, interface: interfaceStack },
    permittedWeights: [400, 500, 600, 700],
    faceEvidence: [],
    checksumPolicy: 'NOT_APPLICABLE_NO_FONT_BYTES_EMBEDDED',
    unsupportedWeightSubstitutions: { 900: 700 },
    syntheticWeightsAllowed: false,
  },
  pdf: {
    version: TYPOGRAPHY_ADAPTER_PROFILE_VERSION,
    tokenSchemaVersion: DESIGN_TOKEN_SCHEMA_VERSION,
    channel: 'pdf',
    status: 'candidate_only',
    delivery: 'fallback_stack_only',
    targets: ['signed-document', 'statement', 'invoice', 'report', 'export'],
    familyStacks: { display: displayStack, interface: interfaceStack },
    permittedWeights: [400, 500, 600, 700],
    faceEvidence: [],
    checksumPolicy: 'NOT_APPLICABLE_NO_FONT_BYTES_EMBEDDED',
    unsupportedWeightSubstitutions: { 900: 700 },
    syntheticWeightsAllowed: false,
  },
} as const satisfies Record<TypographyChannel, TypographyAdapterProfile>;

export function resolveTypographyWeight(
  channel: TypographyChannel,
  role: TypographyRole,
): SupportedFontWeight {
  const profile = TYPOGRAPHY_ADAPTER_PROFILES[channel];
  const requested = TYPOGRAPHY_ROLES[role].fontWeight;

  if ((profile.permittedWeights as readonly SupportedFontWeight[]).includes(requested)) {
    return requested;
  }

  const substitutions = profile.unsupportedWeightSubstitutions as Readonly<
    Partial<Record<SupportedFontWeight, SupportedFontWeight>>
  >;
  const substitute = substitutions[requested];
  if (
    substitute !== undefined &&
    (profile.permittedWeights as readonly SupportedFontWeight[]).includes(substitute)
  ) {
    return substitute;
  }

  throw new Error(`typography_weight_unavailable:${channel}:${role}:${requested}`);
}
